import { Node, mergeAttributes } from "@tiptap/core";
import { toYouTubeNoCookieEmbedSrc } from "src/lib/utils/youtube-privacy";

export interface IframeOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: { src: string }) => ReturnType;
    };
  }
}

export const Iframe = Node.create<IframeOptions>({
  name: "iframe",

  group: "block",

  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: "100%",
      },
      height: {
        default: "400",
      },
      frameborder: {
        default: "0",
      },
      allow: {
        default:
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
      },
      allowfullscreen: {
        default: true,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="iframe"]',
        getAttrs: (node) => {
          if (typeof node === "string") return false;
          const iframe = node.querySelector("iframe");
          if (!iframe) return false;
          return {
            src:
              toYouTubeNoCookieEmbedSrc(iframe.getAttribute("src") || "") ||
              iframe.getAttribute("src"),
            width: iframe.getAttribute("width"),
            height: iframe.getAttribute("height"),
            frameborder: iframe.getAttribute("frameborder"),
            allow: iframe.getAttribute("allow"),
            allowfullscreen: iframe.getAttribute("allowfullscreen"),
          };
        },
      },
      {
        tag: "iframe",
        getAttrs: (node) => {
          if (typeof node === "string") return false;
          return {
            src:
              toYouTubeNoCookieEmbedSrc(node.getAttribute("src") || "") || node.getAttribute("src"),
            width: node.getAttribute("width"),
            height: node.getAttribute("height"),
            frameborder: node.getAttribute("frameborder"),
            allow: node.getAttribute("allow"),
            allowfullscreen: node.getAttribute("allowfullscreen"),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src
      ? toYouTubeNoCookieEmbedSrc(String(HTMLAttributes.src))
      : HTMLAttributes.src;
    const iframeAttrs = mergeAttributes(
      {
        style: "position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;",
        allowfullscreen: "true",
        frameborder: "0",
        allow:
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        referrerpolicy: "strict-origin-when-cross-origin",
      },
      this.options.HTMLAttributes,
      { ...HTMLAttributes, src },
    );

    return [
      "div",
      {
        "data-type": "iframe",
        class: "iframe-container",
        style: "position: relative; width: 100%; height: 400px; margin-bottom: 1rem;",
      },
      ["iframe", iframeAttrs],
    ];
  },

  addCommands() {
    return {
      setIframe:
        (options) =>
        ({ commands }) => {
          const src = options.src
            ? toYouTubeNoCookieEmbedSrc(String(options.src)) || options.src
            : options.src;
          return commands.insertContent({
            type: this.name,
            attrs: { ...options, src },
          });
        },
    };
  },
});
